import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { motion, useInView } from 'framer-motion';
import {
  Globe, ChevronDown, ArrowRight, Mic, Cpu, Smartphone, QrCode,
  Languages, Monitor, Headphones, FileText, Palette, Shield,
  Clock, Users, Building2, GraduationCap, Stethoscope, Landmark,
  Presentation, Radio, ChevronRight
} from 'lucide-react';

const UI_STRINGS = {
  pt: {
    heroTitle: 'Tradução simultânea por IA para eventos ao vivo',
    heroSubtitle: 'Leve a tradução em tempo quase real ao smartphone de cada participante — sem cabines, sem recetores físicos, com operação técnica no local pela Do IT Happen.',
    cta1: 'Pedir demo',
    cta2: 'Ver como funciona',
    howTitle: 'Como funciona',
    howSteps: [
      { title: 'Setup Técnico', desc: 'A equipa Do IT Happen instala e configura todo o sistema de áudio e tradução no local do evento.' },
      { title: 'Captação de Áudio', desc: 'O áudio do orador é captado pela mesa de mistura ou microfone dedicado e enviado para o sistema.' },
      { title: 'Processamento IA', desc: 'A inteligência artificial transcreve, traduz e gera voz em tempo quase real para os idiomas configurados.' },
      { title: 'Acesso Participante', desc: 'Cada participante lê o QR code e acede à tradução no seu smartphone — texto e áudio, sem instalar apps.' },
    ],
    benefitsTitle: 'Vantagens',
    benefits: [
      { icon: 'QrCode', title: 'Acesso por QR Code', desc: 'Sem apps para instalar. O participante lê o código e acede imediatamente.' },
      { icon: 'Shield', title: 'Sem Cabines Físicas', desc: 'Elimine cabines de tradução e recetores. Tudo acontece no smartphone.' },
      { icon: 'Languages', title: 'Múltiplos Idiomas', desc: 'Tradução simultânea para vários idiomas em paralelo, configuráveis por evento.' },
      { icon: 'Smartphone', title: 'Vista Mobile', desc: 'Interface otimizada para smartphone com texto e áudio sincronizados.' },
      { icon: 'Monitor', title: 'Ecrãs de Sala', desc: 'Legendas em ecrãs LCD ou projetores via chroma key para audiências maiores.' },
      { icon: 'Users', title: 'Operação Técnica Local', desc: 'Equipa Do IT Happen presente no evento para setup, monitorização e suporte.' },
      { icon: 'FileText', title: 'Transcrições Pós-Evento', desc: 'Receba a transcrição completa e resumos automáticos após o evento.' },
      { icon: 'Palette', title: 'Branding do Evento', desc: 'Interface personalizável com logótipo e cores do evento ou organização.' },
      { icon: 'Headphones', title: 'Áudio e Texto', desc: 'O participante pode ouvir a tradução em voz e ler as legendas em simultâneo.' },
    ],
    useCasesTitle: 'Casos de Uso',
    useCases: [
      { icon: 'Landmark', title: 'Congressos' },
      { icon: 'Building2', title: 'Townhalls Corporate' },
      { icon: 'Globe', title: 'Conferências Híbridas' },
      { icon: 'GraduationCap', title: 'Formações' },
      { icon: 'Users', title: 'Cimeiras Internacionais' },
      { icon: 'Presentation', title: 'Lançamentos de Produto' },
      { icon: 'Shield', title: 'Reuniões de Board' },
      { icon: 'Radio', title: 'Feiras e Exposições' },
    ],
    validationTitle: 'Já testado em contexto real',
    validationText: 'Sessão de 2 horas com tradução de orador e vídeo, participantes em smartphone e visualização em ecrã de sala.',
    trustTitle: 'Compromisso de Qualidade',
    trustItems: [
      { title: 'Operação Acompanhada', desc: 'Cada evento é operado por um técnico Do IT Happen no local, garantindo a melhor experiência possível.' },
      { title: 'Transparência', desc: 'A qualidade da tradução depende do áudio, rede e contexto. Comunicamos sempre as condições reais.' },
      { title: 'Posicionamento Responsável', desc: 'Solução otimizada para eventos corporativos. Não substitui intérpretes humanos certificados em contextos médicos ou jurídicos oficiais.' },
    ],
    finalCtaTitle: 'Quer levar tradução em tempo real ao seu próximo evento?',
    finalCta1: 'Falar com a equipa',
    finalCta2: 'Agendar demonstração',
    joinSession: 'Entrar numa sessão',
    sessionCode: 'Código da sessão',
    join: 'Entrar',
    hostLogin: 'Hosts',
    poweredBy: 'Do IT Happen',
  },
  en: {
    heroTitle: 'AI simultaneous translation for live events',
    heroSubtitle: 'Deliver near real-time translation to every participant\'s smartphone — no booths, no physical receivers, with on-site technical support by Do IT Happen.',
    cta1: 'Request demo',
    cta2: 'See how it works',
    howTitle: 'How it works',
    howSteps: [
      { title: 'Technical Setup', desc: 'The Do IT Happen team installs and configures the full audio and translation system on-site.' },
      { title: 'Audio Capture', desc: 'The speaker\'s audio is captured from the mixing desk or dedicated microphone and sent to the system.' },
      { title: 'AI Processing', desc: 'AI transcribes, translates and generates voice in near real-time for the configured languages.' },
      { title: 'Participant Access', desc: 'Each participant scans the QR code and accesses translation on their smartphone — text and audio, no apps needed.' },
    ],
    benefitsTitle: 'Benefits',
    benefits: [
      { icon: 'QrCode', title: 'QR Code Access', desc: 'No apps to install. Scan and access immediately.' },
      { icon: 'Shield', title: 'No Physical Booths', desc: 'Eliminate translation booths and receivers. Everything on the smartphone.' },
      { icon: 'Languages', title: 'Multiple Languages', desc: 'Simultaneous translation to multiple languages in parallel.' },
      { icon: 'Smartphone', title: 'Mobile View', desc: 'Optimized smartphone interface with synced text and audio.' },
      { icon: 'Monitor', title: 'Room Displays', desc: 'Subtitles on LCD screens or projectors via chroma key.' },
      { icon: 'Users', title: 'On-site Operation', desc: 'Do IT Happen team present for setup, monitoring and support.' },
      { icon: 'FileText', title: 'Post-event Transcripts', desc: 'Receive full transcription and automatic summaries after the event.' },
      { icon: 'Palette', title: 'Event Branding', desc: 'Customizable interface with event logos and colors.' },
      { icon: 'Headphones', title: 'Audio & Text', desc: 'Participants can listen to translated voice and read subtitles simultaneously.' },
    ],
    useCasesTitle: 'Use Cases',
    useCases: [
      { icon: 'Landmark', title: 'Congresses' },
      { icon: 'Building2', title: 'Corporate Townhalls' },
      { icon: 'Globe', title: 'Hybrid Conferences' },
      { icon: 'GraduationCap', title: 'Training Sessions' },
      { icon: 'Users', title: 'International Summits' },
      { icon: 'Presentation', title: 'Product Launches' },
      { icon: 'Shield', title: 'Board Meetings' },
      { icon: 'Radio', title: 'Trade Shows' },
    ],
    validationTitle: 'Tested in real event conditions',
    validationText: '2-hour session with speaker and video translation, participants on smartphones and room screen display.',
    trustTitle: 'Quality Commitment',
    trustItems: [
      { title: 'Managed Operation', desc: 'Each event is operated by a Do IT Happen technician on-site, ensuring the best possible experience.' },
      { title: 'Transparency', desc: 'Translation quality depends on audio, network and context. We always communicate real conditions.' },
      { title: 'Responsible Positioning', desc: 'Optimized for corporate events. Does not replace certified human interpreters in official medical or legal contexts.' },
    ],
    finalCtaTitle: 'Ready to bring real-time translation to your next event?',
    finalCta1: 'Talk to our team',
    finalCta2: 'Schedule a demo',
    joinSession: 'Join a session',
    sessionCode: 'Session code',
    join: 'Join',
    hostLogin: 'Hosts',
    poweredBy: 'Do IT Happen',
  },
};

const ICON_MAP = {
  QrCode, Shield, Languages, Smartphone, Monitor, Users, FileText, Palette, Headphones,
  Landmark, Building2, Globe, GraduationCap, Presentation, Radio,
};

// Animated waveform component
const Waveform = () => {
  const bars = 24;
  return (
    <div className="flex items-center gap-[2px] h-8">
      {Array.from({ length: bars }).map((_, i) => (
        <motion.div
          key={i}
          className="w-[3px] rounded-full bg-[#00F0FF]"
          animate={{ height: [4, Math.random() * 28 + 4, 4] }}
          transition={{ duration: 0.8 + Math.random() * 0.6, repeat: Infinity, delay: i * 0.05, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
};

// Hero demo panel with typewriter + language badge sync
const HeroDemoPanel = ({ entries, langs }) => {
  const [index, setIndex] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [charIndex, setCharIndex] = useState(0);
  const currentLang = entries[index]?.lang || 'PT';

  useEffect(() => {
    const text = entries[index].text;
    if (charIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayed(text.slice(0, charIndex + 1));
        setCharIndex(charIndex + 1);
      }, 30 + Math.random() * 20);
      return () => clearTimeout(timer);
    } else {
      const timer = setTimeout(() => {
        setIndex((index + 1) % entries.length);
        setCharIndex(0);
        setDisplayed('');
      }, 2200);
      return () => clearTimeout(timer);
    }
  }, [charIndex, index, entries]);

  return (
    <>
      {/* Transcription */}
      <div className="bg-[#131B33] rounded-xl p-4 border border-white/5">
        <div className="flex items-center gap-2 mb-2">
          <Cpu className="w-3.5 h-3.5 text-[#00F0FF]" />
          <span className="text-[10px] text-[#00F0FF] font-['Inter'] uppercase tracking-wider">AI Processing</span>
          <span className="text-[10px] text-slate-500 font-['Inter'] ml-auto">{currentLang}</span>
        </div>
        <div className="font-['Inter'] text-sm text-white/90 h-6">
          {displayed}<span className="animate-pulse">|</span>
        </div>
      </div>
      {/* Language badges */}
      <div className="flex gap-2 flex-wrap">
        {langs.map((lang) => (
          <div
            key={lang}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold font-['Inter'] transition-all duration-300"
            style={{
              background: lang === currentLang ? '#00F0FF' : 'rgba(255,255,255,0.05)',
              color: lang === currentLang ? '#050814' : '#94A3B8',
              border: lang === currentLang ? 'none' : '1px solid rgba(255,255,255,0.1)',
              transform: lang === currentLang ? 'scale(1.05)' : 'scale(1)',
            }}
          >
            {lang}
          </div>
        ))}
      </div>
    </>
  );
};

// Section wrapper with fade-in
const Section = ({ children, className = '', id }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section
      id={id}
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

export default function Landing() {
  const navigate = useNavigate();
  const [sessionCode, setSessionCode] = useState('');
  const [uiLang, setUiLang] = useState(() => {
    const saved = localStorage.getItem('landing_lang');
    if (saved && UI_STRINGS[saved]) return saved;
    const browserLang = navigator.language?.slice(0, 2);
    return browserLang === 'pt' ? 'pt' : 'en';
  });

  const t = UI_STRINGS[uiLang];

  const toggleLang = () => {
    const next = uiLang === 'pt' ? 'en' : 'pt';
    setUiLang(next);
    localStorage.setItem('landing_lang', next);
  };

  const handleJoinSession = () => {
    if (sessionCode.trim()) {
      navigate(`/join/${sessionCode.trim().toUpperCase()}`);
    }
  };

  const scrollToHow = () => {
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
  };

  const demoTranslations = [
    { lang: 'PT', text: 'Bem-vindos a este evento internacional...' },
    { lang: 'EN', text: 'Welcome to this international event...' },
    { lang: 'FR', text: 'Bienvenue à cet événement international...' },
    { lang: 'IT', text: 'Benvenuti a questo evento internazionale...' },
    { lang: 'ES', text: 'Bienvenidos a este evento internacional...' },
    { lang: 'DE', text: 'Willkommen zu dieser internationalen Veranstaltung...' },
    { lang: 'PT', text: 'Os resultados mostram um crescimento de 40%...' },
    { lang: 'EN', text: 'Results show a 40% growth in revenue...' },
    { lang: 'FR', text: 'Les résultats montrent une croissance de 40%...' },
    { lang: 'IT', text: 'I risultati mostrano una crescita del 40%...' },
    { lang: 'ES', text: 'Los resultados muestran un crecimiento del 40%...' },
    { lang: 'DE', text: 'Die Ergebnisse zeigen ein Wachstum von 40%...' },
  ];

  const SUPPORTED_LANGS = ['PT', 'EN', 'FR', 'IT', 'ES', 'DE'];

  return (
    <div className="min-h-screen text-white" style={{ background: '#050814' }} data-testid="landing-page">

      {/* ========== STICKY HEADER ========== */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b border-white/5" style={{ background: 'rgba(5,8,20,0.8)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-dih.png" alt="Do IT Happen" className="h-8" />
            <span className="font-['Cabinet_Grotesk'] font-bold text-lg text-white hidden sm:block">Translate</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-2 py-1">
              <Input
                type="text"
                placeholder={t.sessionCode}
                value={sessionCode}
                onChange={(e) => setSessionCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleJoinSession()}
                className="w-28 h-7 text-xs bg-transparent border-0 text-white placeholder:text-slate-500 focus-visible:ring-0 uppercase tracking-wider"
                data-testid="header-session-code-input"
              />
              <button
                onClick={handleJoinSession}
                disabled={!sessionCode.trim()}
                className="p-1 rounded hover:bg-white/10 text-[#00F0FF] disabled:text-slate-600 transition-colors"
                data-testid="header-join-btn"
              >
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => navigate(localStorage.getItem('token') ? '/host' : '/login')}
              className="text-xs font-medium text-[#00F0FF] hover:text-white transition-colors px-3 py-1.5 rounded-md border border-[#00F0FF]/30 hover:border-[#00F0FF]/60"
              data-testid="host-login-link"
            >
              {t.hostLogin}
            </button>
            <button
              onClick={toggleLang}
              className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/5 border border-white/10 text-xs text-slate-300 hover:text-white transition-colors"
              data-testid="lang-toggle-btn"
            >
              <Globe className="w-3.5 h-3.5" />
              {uiLang.toUpperCase()}
            </button>
          </div>
        </div>
      </header>

      {/* ========== HERO ========== */}
      <section className="relative pt-28 sm:pt-36 pb-20 sm:pb-32 overflow-hidden" data-testid="hero-section">
        {/* Abstract bg */}
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/85149eed-2959-48a7-8c34-4d152ac27e63/images/5d1676e393873d871c8f54775281a21bb5c16f5f681f945d67b594de69472a2b.png"
            alt="" className="w-full h-full object-cover" loading="eager"
          />
        </div>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(5,8,20,0.6), rgba(5,8,20,1))' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left — Copy */}
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <h1
                className="font-['Cabinet_Grotesk'] text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-6"
                data-testid="hero-title"
              >
                {t.heroTitle}
              </h1>
              <p className="font-['Inter'] text-base sm:text-lg text-slate-400 leading-relaxed mb-8 max-w-xl" data-testid="hero-subtitle">
                {t.heroSubtitle}
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  className="px-6 py-5 text-sm font-semibold rounded-lg text-black"
                  style={{ background: '#00F0FF' }}
                  onClick={() => window.open('mailto:info@doithappen.pt?subject=Demo Do IT Translate', '_blank')}
                  data-testid="hero-cta-demo"
                >
                  {t.cta1}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  className="px-6 py-5 text-sm font-semibold rounded-lg border-white/20 text-white hover:bg-white/5"
                  onClick={scrollToHow}
                  data-testid="hero-cta-how"
                >
                  {t.cta2}
                </Button>
              </div>
            </motion.div>

            {/* Right — Visual Demo */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-[#0C1222] border border-white/10 rounded-2xl p-6 space-y-4" data-testid="hero-demo">
                {/* Step 1: Speaker */}
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center">
                    <Mic className="w-4 h-4 text-[#00F0FF]" />
                  </div>
                  <span className="text-xs text-slate-400 font-['Inter']">Speaker</span>
                  <div className="flex-1"><Waveform /></div>
                </div>

                {/* Step 2: Transcription with language cycling */}
                <HeroDemoPanel entries={demoTranslations} langs={SUPPORTED_LANGS} />

                {/* Step 4: Phone mockup */}
                <div className="flex items-center gap-4 pt-2">
                  <div className="w-16 h-28 rounded-xl border-2 border-white/20 bg-[#050814] flex items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-1 rounded-lg bg-[#0C1222] flex flex-col justify-end p-1.5">
                      <div className="h-1 bg-[#00F0FF]/40 rounded mb-0.5 w-full" />
                      <div className="h-1 bg-white/20 rounded w-3/4" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-slate-400 font-['Inter']">{uiLang === 'pt' ? 'Vista do participante' : 'Participant view'}</p>
                    <p className="text-[10px] text-slate-500 font-['Inter'] mt-0.5">{uiLang === 'pt' ? 'Texto + áudio no smartphone' : 'Text + audio on smartphone'}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========== HOW IT WORKS ========== */}
      <Section id="how-it-works" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Cabinet_Grotesk'] text-3xl sm:text-4xl font-bold text-center mb-4 tracking-tight" data-testid="how-title">
            {t.howTitle}
          </h2>
          <div className="w-12 h-1 bg-[#00F0FF] mx-auto rounded-full mb-12 sm:mb-16" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.howSteps.map((step, i) => {
              const icons = [Mic, Radio, Cpu, Smartphone];
              const StepIcon = icons[i];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12 }}
                  className="relative bg-[#0C1222] border border-white/10 rounded-xl p-6 hover:border-[#00F0FF]/30 transition-colors"
                  data-testid={`how-step-${i}`}
                >
                  <div className="text-[#00F0FF] text-5xl font-['Cabinet_Grotesk'] font-extrabold opacity-10 absolute top-3 right-4">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center mb-4">
                    <StepIcon className="w-5 h-5 text-[#00F0FF]" />
                  </div>
                  <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold text-white mb-2">{step.title}</h3>
                  <p className="font-['Inter'] text-sm text-slate-400 leading-relaxed">{step.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ========== BENEFITS ========== */}
      <Section className="py-20 sm:py-28" style={{ background: '#0C1222' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Cabinet_Grotesk'] text-3xl sm:text-4xl font-bold text-center mb-4 tracking-tight" data-testid="benefits-title">
            {t.benefitsTitle}
          </h2>
          <div className="w-12 h-1 bg-[#00F0FF] mx-auto rounded-full mb-12 sm:mb-16" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.benefits.map((b, i) => {
              const Icon = ICON_MAP[b.icon] || Globe;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="bg-[#050814] border border-white/10 rounded-xl p-6 hover:border-[#00F0FF]/20 transition-all group"
                  data-testid={`benefit-${i}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center mb-4 group-hover:bg-[#00F0FF]/20 transition-colors">
                    <Icon className="w-5 h-5 text-[#00F0FF]" />
                  </div>
                  <h3 className="font-['Cabinet_Grotesk'] text-base font-bold text-white mb-1.5">{b.title}</h3>
                  <p className="font-['Inter'] text-sm text-slate-400 leading-relaxed">{b.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ========== USE CASES ========== */}
      <Section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Cabinet_Grotesk'] text-3xl sm:text-4xl font-bold text-center mb-4 tracking-tight" data-testid="usecases-title">
            {t.useCasesTitle}
          </h2>
          <div className="w-12 h-1 bg-[#00F0FF] mx-auto rounded-full mb-12 sm:mb-16" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {t.useCases.map((uc, i) => {
              const Icon = ICON_MAP[uc.icon] || Globe;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="bg-[#0C1222] border border-white/10 rounded-xl p-5 text-center hover:border-[#00F0FF]/20 transition-all"
                  data-testid={`usecase-${i}`}
                >
                  <Icon className="w-6 h-6 text-[#00F0FF] mx-auto mb-3" />
                  <p className="font-['Cabinet_Grotesk'] text-sm font-semibold text-white">{uc.title}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ========== VALIDATION ========== */}
      <Section className="py-20 sm:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://images.unsplash.com/photo-1558008258-3256797b43f3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxjb3JhdGUlMjBldmVudCUyMHNwZWFrZXIlMjBzdGFnZXxlbnwwfHx8fDE3NzkwNDU5NTd8MA&ixlib=rb-4.1.0&q=85"
            alt="" className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, #050814, rgba(5,8,20,0.85), #050814)' }} />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/20 mb-6">
            <div className="w-2 h-2 rounded-full bg-[#00F0FF] animate-pulse" />
            <span className="text-xs text-[#00F0FF] font-['Inter'] font-medium">{uiLang === 'pt' ? 'Validado em produção' : 'Validated in production'}</span>
          </div>
          <h2 className="font-['Cabinet_Grotesk'] text-3xl sm:text-4xl font-bold mb-6 tracking-tight" data-testid="validation-title">
            {t.validationTitle}
          </h2>
          <p className="font-['Inter'] text-lg text-slate-300 leading-relaxed" data-testid="validation-text">
            "{t.validationText}"
          </p>
        </div>
      </Section>

      {/* ========== TRUST ========== */}
      <Section className="py-20 sm:py-28" style={{ background: '#0C1222' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Cabinet_Grotesk'] text-3xl sm:text-4xl font-bold text-center mb-4 tracking-tight" data-testid="trust-title">
            {t.trustTitle}
          </h2>
          <div className="w-12 h-1 bg-[#00F0FF] mx-auto rounded-full mb-12 sm:mb-16" />
          <div className="grid md:grid-cols-3 gap-6">
            {t.trustItems.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#050814] border border-white/10 rounded-xl p-6"
                data-testid={`trust-${i}`}
              >
                <h3 className="font-['Cabinet_Grotesk'] text-base font-bold text-white mb-2">{item.title}</h3>
                <p className="font-['Inter'] text-sm text-slate-400 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ========== FINAL CTA ========== */}
      <Section className="py-24 sm:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Cabinet_Grotesk'] text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-8" data-testid="final-cta-title">
            {t.finalCtaTitle}
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              className="px-8 py-6 text-base font-semibold rounded-lg text-black"
              style={{ background: '#00F0FF' }}
              onClick={() => window.open('mailto:info@doithappen.pt?subject=Demo Do IT Translate', '_blank')}
              data-testid="final-cta-demo"
            >
              {t.finalCta2}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              variant="outline"
              className="px-8 py-6 text-base font-semibold rounded-lg border-white/20 text-white hover:bg-white/5"
              onClick={() => window.open('mailto:info@doithappen.pt?subject=Contacto Do IT Translate', '_blank')}
              data-testid="final-cta-contact"
            >
              {t.finalCta1}
            </Button>
          </div>
        </div>
      </Section>

      {/* ========== MOBILE JOIN (visible on small screens) ========== */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 backdrop-blur-xl border-t border-white/10 px-4 py-3" style={{ background: 'rgba(5,8,20,0.9)' }}>
        <div className="flex gap-2">
          <Input
            type="text"
            placeholder={t.sessionCode}
            value={sessionCode}
            onChange={(e) => setSessionCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && handleJoinSession()}
            className="flex-1 h-10 text-sm bg-white/5 border-white/10 text-white placeholder:text-slate-500 uppercase tracking-wider"
            data-testid="mobile-session-code-input"
          />
          <Button
            onClick={handleJoinSession}
            disabled={!sessionCode.trim()}
            className="h-10 px-4 text-black font-semibold"
            style={{ background: sessionCode.trim() ? '#00F0FF' : 'rgba(0,240,255,0.3)' }}
            data-testid="mobile-join-btn"
          >
            {t.join}
          </Button>
        </div>
      </div>

      {/* ========== FOOTER ========== */}
      <footer className="py-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo-dih.png" alt="Do IT Happen" className="h-6" />
            <span className="text-xs text-slate-500 font-['Inter']">Do IT Translate</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/login')}
              className="text-xs text-slate-500 hover:text-slate-300 transition-colors font-['Inter']"
              data-testid="footer-login-link"
            >
              {t.hostLogin}
            </button>
            <span className="text-xs text-slate-600">|</span>
            <span className="text-xs text-slate-500 font-['Inter']">info@doithappen.pt</span>
          </div>
        </div>
      </footer>

      {/* Bottom padding for mobile join bar */}
      <div className="md:hidden h-16" />
    </div>
  );
}
